import React, { useState, useEffect } from 'react';
import { Plus, Edit, Edit2, RefreshCw, Trash2, Save, X } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { usePermissions } from '@hooks/usePermissions';
import { acquirerService } from '@services/acquirerService';
import { Acquirer, AcquirerCardTax, AcquirerCardTaxFormData } from '@/types/acquirer';
import { formatEC, parseTaxRateInput } from '../receivingHubUtils';

/**
 * Linha da listagem com o estado da edição inline da taxa. A taxa é o campo que muda
 * com frequência (renegociação com o adquirente), então é editável direto na linha;
 * os demais campos continuam pelo formulário de edição.
 */
interface CardTaxRow extends AcquirerCardTax {
  isEditing: boolean;
  /** Texto do input enquanto edita (aceita vírgula, validado no salvar). */
  tempTaxRate: string;
  isSaving: boolean;
  errorMessage?: string;
}

const toRow = (item: AcquirerCardTax): CardTaxRow => ({
  ...item,
  isEditing: false,
  tempTaxRate: item.tax_rate.toFixed(2),
  isSaving: false,
  errorMessage: undefined,
});

const emptyForm: AcquirerCardTaxFormData = {
  brand: '',
  tax_rate: 0,
  card_type: 'C',
  minum_tax: null,
  min_installments: 1,
  max_installments: 1,
  ec: null,
  acquirer_id: 0,
};

const CARD_TYPES: { value: string; label: string }[] = [
  { value: 'C', label: 'Crédito' },
  { value: 'D', label: 'Débito' },
];

export const AcquirerCardTaxTab: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('receiving_hub.write').hasPermission;

  const [items, setItems] = useState<CardTaxRow[]>([]);
  const [acquirers, setAcquirers] = useState<Acquirer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<AcquirerCardTax | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<AcquirerCardTaxFormData>({ ...emptyForm });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const pageSize = 20;

  const loadLookups = async () => {
    try {
      const acqs = await acquirerService.listAllAcquirers();
      setAcquirers(acqs);
    } catch { /* silently ignore */ }
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await acquirerService.getCardTaxes({ page: currentPage, pageSize });
      setItems(result.data.map(toRow));
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLookups(); }, []);
  useEffect(() => { loadItems(); }, [currentPage]);

  const getAcquirerName = (id: number) => acquirers.find(a => a.id === id)?.name ?? `#${id}`;

  const openCreateForm = () => {
    setEditingItem(null);
    setFormData({ ...emptyForm });
    setFormErrors({});
    setShowForm(true);
  };

  const openEditForm = (item: AcquirerCardTax) => {
    setEditingItem(item);
    setFormData({
      brand: item.brand,
      tax_rate: item.tax_rate,
      card_type: item.card_type,
      minum_tax: item.minum_tax,
      min_installments: item.min_installments,
      max_installments: item.max_installments,
      ec: item.ec,
      acquirer_id: item.acquirer_id,
    });
    setFormErrors({});
    setShowForm(true);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.acquirer_id) errors.acquirer_id = 'Adquirente é obrigatório';
    if (!formData.brand.trim()) errors.brand = 'Bandeira é obrigatória';
    if (formData.brand.length > 16) errors.brand = 'Bandeira deve ter no máximo 16 caracteres';
    if (formData.tax_rate < 0 || formData.tax_rate > 99.99) errors.tax_rate = 'Taxa deve ser entre 0 e 99.99';
    if (!formData.card_type) errors.card_type = 'Tipo de cartão é obrigatório';
    if (formData.min_installments < 1) errors.min_installments = 'Mínimo de parcelas deve ser >= 1';
    if (formData.max_installments < formData.min_installments) errors.max_installments = 'Máximo deve ser >= mínimo de parcelas';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      setError(null);
      if (editingItem) {
        await acquirerService.updateCardTax(editingItem.id, formData);
      } else {
        await acquirerService.createCardTax(formData);
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await acquirerService.deleteCardTax(id);
      setDeleteConfirm(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  // ─── Edição inline da taxa ───────────────────────────────────

  const updateRow = (id: number, fields: Partial<CardTaxRow>) =>
    setItems(prev => prev.map(row => (row.id === id ? { ...row, ...fields } : row)));

  const startEditRow = (row: CardTaxRow) =>
    updateRow(row.id, { isEditing: true, tempTaxRate: row.tax_rate.toFixed(2), errorMessage: undefined });

  const cancelEditRow = (row: CardTaxRow) =>
    updateRow(row.id, { isEditing: false, tempTaxRate: row.tax_rate.toFixed(2), errorMessage: undefined });

  /**
   * Salva só a taxa da linha. O PUT do backend recebe o registro inteiro, então os demais
   * campos vão inalterados a partir da própria linha; a resposta repovoa a linha, sem
   * recarregar a listagem (mantém página e posição da rolagem).
   */
  const saveRow = async (row: CardTaxRow) => {
    const parsed = parseTaxRateInput(row.tempTaxRate);
    if (!parsed.ok) {
      updateRow(row.id, { errorMessage: parsed.error });
      return;
    }
    if (parsed.value === row.tax_rate) {
      cancelEditRow(row);
      return;
    }

    updateRow(row.id, { isSaving: true, errorMessage: undefined });
    try {
      const updated = await acquirerService.updateCardTax(row.id, {
        brand: row.brand,
        tax_rate: parsed.value,
        card_type: row.card_type,
        minum_tax: row.minum_tax,
        min_installments: row.min_installments,
        max_installments: row.max_installments,
        ec: row.ec,
        acquirer_id: row.acquirer_id,
      });
      setItems(prev => prev.map(r => (r.id === row.id ? toRow({ ...r, ...updated }) : r)));
    } catch (err) {
      updateRow(row.id, {
        isSaving: false,
        errorMessage: err instanceof Error ? err.message : 'Erro ao salvar a taxa',
      });
    }
  };

  const updateField = (field: keyof AcquirerCardTaxFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Taxas de cartão de crédito/débito por adquirente, EC e bandeira.
          {canWrite && ' Clique na taxa para alterá-la direto na linha.'}
        </p>
        <PermissionGuard permission="receiving_hub.write">
          <Button size="sm" onClick={openCreateForm} icon={<Plus size={16} />}>Nova Taxa</Button>
        </PermissionGuard>
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              {editingItem ? 'Editar Taxa de Cartão' : 'Nova Taxa de Cartão'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Adquirente <span className="text-red-500">*</span></label>
                <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={formData.acquirer_id} onChange={e => updateField('acquirer_id', Number(e.target.value))}>
                  <option value={0}>Selecione...</option>
                  {acquirers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {formErrors.acquirer_id && <p className="text-xs text-red-500 mt-1">{formErrors.acquirer_id}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bandeira <span className="text-red-500">*</span></label>
                <Input value={formData.brand} onChange={e => updateField('brand', e.target.value)} placeholder="Ex: Visa, Mastercard" maxLength={16} />
                {formErrors.brand && <p className="text-xs text-red-500 mt-1">{formErrors.brand}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tipo <span className="text-red-500">*</span></label>
                <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={formData.card_type} onChange={e => updateField('card_type', e.target.value)}>
                  {CARD_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                </select>
                {formErrors.card_type && <p className="text-xs text-red-500 mt-1">{formErrors.card_type}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Taxa (%) <span className="text-red-500">*</span></label>
                <Input type="number" step="0.01" value={formData.tax_rate || ''} onChange={e => updateField('tax_rate', parseFloat(e.target.value) || 0)} placeholder="0.00" />
                {formErrors.tax_rate && <p className="text-xs text-red-500 mt-1">{formErrors.tax_rate}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tarifa mínima (R$)</label>
                <Input type="number" step="0.01" value={formData.minum_tax ?? ''} onChange={e => updateField('minum_tax', e.target.value ? parseFloat(e.target.value) : null)} placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Parcelas Mín. <span className="text-red-500">*</span></label>
                <Input type="number" value={formData.min_installments} onChange={e => updateField('min_installments', parseInt(e.target.value) || 1)} placeholder="1" />
                {formErrors.min_installments && <p className="text-xs text-red-500 mt-1">{formErrors.min_installments}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Parcelas Máx. <span className="text-red-500">*</span></label>
                <Input type="number" value={formData.max_installments} onChange={e => updateField('max_installments', parseInt(e.target.value) || 1)} placeholder="1" />
                {formErrors.max_installments && <p className="text-xs text-red-500 mt-1">{formErrors.max_installments}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">EC</label>
                <Input type="number" value={formData.ec ?? ''} onChange={e => updateField('ec', e.target.value ? Number(e.target.value) : null)} placeholder="Código EC" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)} icon={<X size={14} />}>Cancelar</Button>
              <Button size="sm" onClick={handleSave} loading={saving} icon={<Save size={14} />}>{editingItem ? 'Salvar' : 'Criar'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Adquirente</th>
                <th className="text-left">EC</th>
                <th className="text-left">Bandeira</th>
                <th className="text-center">Tipo</th>
                <th className="text-right">Taxa (%)</th>
                <th className="text-center">Parcelas</th>
                <th className="text-right">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(8)].map((_, j) => (
                      <TableCell key={j}><div className="h-4 bg-slate-200 rounded w-16 animate-pulse" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-500 py-8">Nenhuma taxa encontrada</TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-sm">{item.id}</TableCell>
                    <TableCell>{getAcquirerName(item.acquirer_id)}</TableCell>
                    <TableCell className="font-mono text-sm text-slate-600">{formatEC(item.ec)}</TableCell>
                    <TableCell className="font-medium">{item.brand}</TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.card_type === 'C' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                        {item.card_type === 'C' ? 'Crédito' : 'Débito'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {item.isEditing && canWrite ? (
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center justify-end gap-1">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max="99.99"
                              autoFocus
                              disabled={item.isSaving}
                              className="w-24 rounded-lg border border-slate-300 px-2 py-1 text-sm text-right font-mono"
                              value={item.tempTaxRate}
                              onChange={e => updateRow(item.id, { tempTaxRate: e.target.value, errorMessage: undefined })}
                              onKeyDown={e => {
                                if (e.key === 'Enter') saveRow(item);
                                if (e.key === 'Escape') cancelEditRow(item);
                              }}
                            />
                            <span className="text-sm text-slate-500">%</span>
                          </div>
                          {item.errorMessage && (
                            <p className="text-xs text-red-500">{item.errorMessage}</p>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={!canWrite}
                          onClick={() => startEditRow(item)}
                          title={canWrite ? 'Alterar a taxa nesta linha' : undefined}
                          className={`inline-flex items-center gap-1.5 font-mono ${
                            canWrite ? 'hover:text-emerald-700' : 'cursor-default'
                          }`}
                        >
                          {item.tax_rate.toFixed(2)}%
                          {canWrite && <Edit2 size={12} className="text-slate-400" />}
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-sm">{item.min_installments}x — {item.max_installments}x</TableCell>
                    <TableCell className="text-right">
                      {item.isEditing && canWrite ? (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => saveRow(item)}
                            disabled={item.isSaving}
                            icon={item.isSaving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                          >
                            Salvar
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => cancelEditRow(item)}
                            disabled={item.isSaving}
                            icon={<X size={14} />}
                          >
                            Cancelar
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <PermissionGuard permission="receiving_hub.write">
                            <Button variant="secondary" size="sm" onClick={() => openEditForm(item)} icon={<Edit size={14} />}>Editar</Button>
                          </PermissionGuard>
                          <PermissionGuard permission="receiving_hub.write">
                            {deleteConfirm === item.id ? (
                              <div className="flex items-center gap-1">
                                <Button variant="danger" size="sm" onClick={() => handleDelete(item.id)}>Confirmar</Button>
                                <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                              </div>
                            ) : (
                              <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(item.id)} icon={<Trash2 size={14} />}>Excluir</Button>
                            )}
                          </PermissionGuard>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
            <span className="text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Próxima</Button>
          </div>
        </div>
      )}
    </div>
  );
};
